import { useMemo, useRef } from 'react';
import type { Bonus, Freespin } from '@/api/common';
import { useGetCommonStore } from '@/store';
import { getActiveBonuses, sortBonusesByExpiration } from '../helpers';

export const useActiveBonuses = (): (Bonus | Freespin)[] => {
  const useCommonStore = useGetCommonStore();

  const freespins = useCommonStore.use.freespins();
  const freespinsPending = useCommonStore.use.freespinsPending();
  const bonuses = useCommonStore.use.bonuses();
  const bonusesPending = useCommonStore.use.bonusesPending();

  const initialOrderRef = useRef<number[] | null>(null);

  return useMemo(() => {
    if (bonusesPending || freespinsPending || !bonuses || !freespins) {
      return [];
    }

    const activeBonuses = getActiveBonuses(bonuses, freespins);

    if (initialOrderRef.current === null) {
      const sorted = sortBonusesByExpiration(activeBonuses);
      initialOrderRef.current = sorted.map((bonus) => bonus.id);
      return sorted;
    }

    const orderMap = new Map(
      initialOrderRef.current.map((id, index) => [id, index])
    );

    const existingBonuses = activeBonuses.filter((bonus) =>
      orderMap.has(bonus.id)
    );
    const newBonuses = activeBonuses.filter((bonus) => !orderMap.has(bonus.id));

    existingBonuses.sort(
      (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
    );

    const sortedNewBonuses = sortBonusesByExpiration(newBonuses);
    const result = [...existingBonuses, ...sortedNewBonuses];

    initialOrderRef.current = result.map((bonus) => bonus.id);

    return result;
  }, [bonuses, freespins, bonusesPending, freespinsPending]);
};
